import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, FileText, CheckCircle2, XCircle, ExternalLink, Users, Layers3, Store, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("PENDING_REVIEW");
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    api.get("/admin/summary").then((r) => { setSummary(r.data); setDenied(false); })
      .catch((e) => { if (e.response?.status === 403) { setDenied(true); } });
    api.get("/admin/kyc", { params: { status: filter } })
      .then((r) => setApps(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  const approve = async (uid) => {
    setBusy(true);
    try { await api.post(`/admin/kyc/${uid}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const reject = async () => {
    setBusy(true);
    try { await api.post(`/admin/kyc/${rejectId}/reject`, null, { params: { reason: rejectReason || "Documents do not meet requirements" } }); toast.success("Rejected"); setRejectId(null); setRejectReason(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!user) return <div className="min-h-screen bg-black flex items-center justify-center"><img src="/brand/auvra-logo-trim.png" alt="AUVRA" className="h-10 w-auto animate-pulse" /></div>;

  if (denied) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <ShieldCheck className="w-8 h-8 text-auvra-blue mx-auto mb-4" />
        <h1 className="text-2xl font-semibold tracking-tight">Compliance access required</h1>
        <p className="text-secondary-auvra text-sm mt-3">This console is restricted to the AUVRA compliance team. If you need access, contact <a href="mailto:compliance@auvrarwa.com" className="text-auvra-blue">compliance@auvrarwa.com</a>.</p>
        <Link to="/dashboard" className="inline-block mt-6 text-sm border border-white/15 hover:border-auvra-blue rounded-full px-5 py-2 transition-colors">Back to dashboard</Link>
      </div>
    </div>
  );

  const openDoc = (docId) => {
    const token = localStorage.getItem("auvra_token");
    // Simple fetch → blob → open
    fetch(`${backendUrl}/api/admin/kyc/document/${docId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("Unable to open document"));
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-shell">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-black/85 backdrop-blur-xl">
        <div className="px-6 flex items-center justify-between py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center"><img src="/brand/auvra-logo-trim.png" alt="AUVRA" className="h-7 w-auto" /></Link>
            <span className="text-xs uppercase tracking-[0.2em] text-secondary-auvra">· Compliance Console</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-secondary-auvra hidden sm:block">{user.email}</span>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-secondary-auvra hover:text-white" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="p-6 md:p-10 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-auvra-blue">Compliance</span>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Reviewer Console</h1>
            <p className="text-secondary-auvra text-sm mt-1">Review, approve or reject investor KYC submissions.</p>
          </div>
          <Link to="/dashboard" className="text-sm border border-white/15 hover:border-white/40 rounded-full px-4 py-2 transition-colors">Back to Dashboard</Link>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-summary">
            {[
              { Icon: Users, l: "Users", v: summary.users },
              { Icon: Layers3, l: "Subscriptions", v: summary.subscriptions },
              { Icon: Store, l: "Open Listings", v: summary.open_listings },
              { Icon: Clock, l: "KYC Pending", v: summary.kyc_pending, tone: "yellow" },
              { Icon: CheckCircle2, l: "KYC Approved", v: summary.kyc_approved, tone: "green" },
            ].map((s) => (
              <div key={s.l} className="glass rounded-2xl p-5">
                <s.Icon className={`w-4 h-4 ${s.tone === "yellow" ? "text-yellow-400" : s.tone === "green" ? "text-green-400" : "text-auvra-blue"} mb-3`} />
                <div className="text-xs uppercase tracking-wider text-secondary-auvra">{s.l}</div>
                <div className="text-2xl font-semibold mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-4">
          {[["PENDING_REVIEW", "Pending"], ["APPROVED", "Approved"], ["REJECTED", "Rejected"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${filter === k ? "bg-auvra-blue border-auvra-blue text-white" : "border-white/15 text-secondary-auvra hover:border-white/40"}`}>{l}</button>
          ))}
        </div>

        <div className="space-y-4" data-testid="admin-kyc-list">
          {apps.length === 0 && <div className="glass rounded-2xl p-8 text-center text-secondary-auvra text-sm">No applications for this filter.</div>}
          {apps.map((a) => (
            <div key={a.user_id} className="glass rounded-2xl p-6" data-testid={`app-${a.user_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${a.status === "APPROVED" ? "bg-green-500/10 border-green-500/30 text-green-400" : a.status === "REJECTED" ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-yellow-500/10 border-yellow-500/30 text-yellow-400"}`}>{a.status.replace("_", " ")}</span>
                    <span className="text-[10px] text-secondary-auvra">Submitted {a.submitted_at?.slice(0, 10)}</span>
                  </div>
                  <h4 className="text-lg font-semibold">{a.full_name}</h4>
                  <div className="text-xs text-secondary-auvra mt-1">
                    {a.user?.email} · {a.country} · {a.id_type.replace("_", " ")} · {a.id_number}
                  </div>
                  {a.address && <div className="text-xs text-secondary-auvra mt-1">Address: {a.address}</div>}
                  <div className="text-xs text-secondary-auvra mt-1">Wallet cash: <span className="text-white">${fmt(a.user?.cash || 0)}</span></div>
                </div>
                {a.status === "PENDING_REVIEW" && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => approve(a.user_id)} disabled={busy} data-testid={`approve-${a.user_id}`}
                      className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors">
                      <CheckCircle2 className="w-4 h-4" /> Approve
                    </button>
                    <button onClick={() => setRejectId(a.user_id)} disabled={busy} data-testid={`reject-${a.user_id}`}
                      className="inline-flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm font-medium px-4 py-2 rounded-full transition-colors">
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                  </div>
                )}
              </div>
              {a.documents && a.documents.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-3 mt-5">
                  {a.documents.map((d) => (
                    <button key={d.id} onClick={() => openDoc(d.id)} data-testid={`doc-${d.id}`}
                      className="text-left flex items-center gap-3 border border-white/10 hover:border-auvra-blue rounded-xl p-3 transition-colors">
                      <FileText className="w-4 h-4 text-auvra-blue flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium capitalize">{d.kind.replace("_", " ")}</div>
                        <div className="text-[10px] text-secondary-auvra truncate">{d.filename} · {(d.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <ExternalLink className="w-3 h-3 text-secondary-auvra" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-5 text-xs text-yellow-400/80 bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">No verification documents uploaded — request the investor to upload before approval.</div>
              )}
              {a.status === "REJECTED" && a.rejection_reason && (
                <div className="mt-4 text-xs text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg p-3">Rejected: {a.rejection_reason}</div>
              )}
            </div>
          ))}
        </div>
      </main>

      {rejectId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-6" onClick={() => setRejectId(null)}>
          <div onClick={(e) => e.stopPropagation()} className="glass rounded-2xl p-6 max-w-md w-full" data-testid="reject-modal">
            <h3 className="text-lg font-semibold mb-4">Reject KYC application</h3>
            <label className="block text-xs uppercase tracking-wider text-secondary-auvra mb-2">Reason</label>
            <textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason"
              className="w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue"
              placeholder="Documents do not meet requirements…" />
            <div className="flex gap-3 mt-4 justify-end">
              <button onClick={() => setRejectId(null)} className="border border-white/15 hover:border-white/40 rounded-full px-4 py-2 text-sm">Cancel</button>
              <button onClick={reject} disabled={busy} data-testid="reject-confirm" className="bg-red-500 hover:bg-red-500/90 disabled:opacity-60 text-white rounded-full px-4 py-2 text-sm">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
